import React from "react";
import {
  FontWeights,
  getTheme,
  IconButton,
  mergeStyleSets,
  MessageBar,
  MessageBarType,
  Modal,
  ScrollablePane,
  SharedColors,
  Spinner,
  Stack,
  Text,
} from "@fluentui/react";
import {useBoolean} from "@uifabric/react-hooks";
import {useSelector} from "react-redux";
import {useHistory, useParams} from "react-router-dom";
import {ConfirmationForm} from "../../../components/shared/forms/ConfirmationForm";
import {ServiceForm} from "../../../components/shared/forms/ServiceForm";
import {IBarMessage, ImportFromFile,} from "../../../components/shared/importFromFile/ImportFromFile";
import {CustomPanel} from "../../../components/shared/panel/CustomPanel";
import {SomethingWentWrong} from "../../../components/shared/somethingWentWrong/SomethingWentWrong";
import {portalApi} from "../../../core/api/api";
import {IService, IStore, IWorkflow} from "../../../core/store";
import {IRequestResult} from "../../../core/store/typings/IResponseResult";
import {strings} from "../../../localization/strings";
import {ServiceConfigurationDetails} from "./ServiceConfigurationDetails";
import {ServiceConfigurationHeader} from "./ServiceConfigurationHeader";
import {getLocalizedChannel, getLocalizedDeviceCheckSolution} from "../../../localization/helpers/channel";
import {endpoints} from "../../../core/api/endpoints";
import {
  CheckAssesmentModuleAccess,
  CheckIdentificationModuleAccess,
  CheckProcessStageAccess,
  CheckProcessStageSectionOptionAccess,
  CheckWorkflowSettingsAccess,
  Experience,
} from "../../../components/shared/configurator/helpers/evisibility";
import {
  CatalogTypes,
  CommissionType,
  DiagnosticsModes,
  IdentificationMethods,
  IDiagnosticsConfig,
  IIdentificationSearchFormConfig,
  Inspections,
  ISoftwareConfig,
  OfferProviderTypes,
  ProcessStages,
  ScannerIdentificationTypes,
  ScannerUploadMethods,
  ServiceTypes,
  SoftwareModes,
} from "@piceasoft/core";
import {DeviceCheckSolutions} from "../../../core/store/typings/DeviceCheckSolutions";
import {compareCurrentUTCDateTime} from "../../../core/helpers/date-time";
import { ShareWorkflowModal } from "./ServiceConfigurationDetails/ShareWorkflowModal";
import { getLocalizedStageName } from "../../../localization/helpers/transaction";

const isCheckedRequiredDataExists = (workflow: IWorkflow): boolean => {
  const {serviceType, selfService} = workflow
  if (serviceType === ServiceTypes.SelfService && !selfService) {
    return false
  }
  return true
}
const isSpecificDataMisplaced = (workflow: IWorkflow): boolean => {
  const {serviceType, selfService} = workflow
  if (serviceType !== ServiceTypes.SelfService && selfService) {
    return true
  }
  return false
}
type StageSectionCase = {
  processStage: ProcessStages;
  section: string;
  option: string;
  value: unknown;
  fixOption?(config: IWorkflow): void ;
};
type TProps = {};
const JSON_TYPE = "application/json";

export const ServiceConfiguration: React.FC<TProps> = () => {
  const { serviceId } = useParams<{ serviceId: string }>();
  
  const workflowIconGeneration = useSelector((s: IStore) => s.workplace.organization?.workflowIconGeneration);
  
  const applicationUrl = useSelector((s: IStore) => s.environment.applicationUrl );
  const selfServiceUrl = useSelector((s: IStore) => s.environment.selfServiceUrl );

  const [serviceState, setServiceState] = 
    React.useState<{ qrCodeLink: string; fetched: boolean; data?: IService;}>({ fetched: false, qrCodeLink: "" });
  const [error, setError] = React.useState<string>();
  const [workflowFileMessages, setWorflowFileMessages] = React.useState<IBarMessage[]>();
  const [formError, setFormError] = React.useState<string>();

  const [confirmation, { setTrue: showConfirmation, setFalse: hideConfirmation }] = useBoolean(false);
  const [isPanelOpen, { setTrue: showPanel, setFalse: hidePanel }] = useBoolean(false);
  const [fetching, { setTrue: startFetch, setFalse: stopFetch }] = useBoolean(false);
  const [isImportModalShowed, { setTrue: showImportModal, setFalse: hideImportModal }] = useBoolean(false);
  const [infoFetching, { setTrue: startFetchInfo, setFalse: stopFetchInfo }] = useBoolean(false);
  const [infoWasSaved, setInfoWasSaved] = React.useState(false);
  const [workflowIsChecking, { setTrue: startWorkflowCheck, setFalse: stopWorkflowCheck }] = useBoolean(false);
  const [isShareWorkflowPanelOpen, { setTrue: showShareWorkflow, setFalse: hideShareWorkflow }] = useBoolean(false);
  
  const [confirmIsDisabled, setConfirmDisabled] = React.useState(false);

  const history = useHistory();
  
  React.useEffect(() => {
    getService(parseInt(serviceId));
  }, []);

  const checkWorkflowForUpload = async (file: File): Promise<File | void> => {
    startWorkflowCheck();
    let messages: IBarMessage[] = [];
    let fixedWorkflowFile: File | undefined;
    let hasBlockingErrors = false;
    /**
     * Function to push a message to a messages array.
     * @param {string} text - The text of the message to push.
     * @param {MessageBarType} [type=MessageBarType.error] - The type of the message to push. Defaults to `MessageBarType.error`.
     */
    const pushMessage = (
      text: string,
      type: MessageBarType = MessageBarType.error
    ) => {
      messages.push({ text, type });
    };
    const blockConfirm = () => {
      hasBlockingErrors = true;
      setConfirmDisabled(true);
    };

    if (file.type === JSON_TYPE) {
      try {
        const fileUrl = URL.createObjectURL(file);
        const workflowLoaded = (await (
          await fetch(fileUrl)
        ).json()) as IWorkflow;
        URL.revokeObjectURL(fileUrl);
        
        setConfirmDisabled(false);
        let workflowWasFixed = false;
        const channelLoaded: Experience | undefined = workflowLoaded.channel;
        const serviceStateChannel = serviceState.data?.channel;

        let hasCatalogWarning = false;
        let hasPromotionWarning = false;

        const catalogType = workflowLoaded?.useDiscountCatalogs 
            ? CatalogTypes.GradeDiscount 
            : CatalogTypes.Tradein
        
        let catalogsIds: string[] = [];
        
        const catalogsResult =
          await portalApi.organization.catalogs.getCatalogsByCatalogType(catalogType);
        
        if (catalogsResult.successed && catalogsResult.data) {
          catalogsIds = catalogsResult.data
            .filter((i) => !i.removed)
            .map((i) => i.id);
        }
        
        if (!workflowLoaded.channel) {
          pushMessage(strings.CONSTRUCTOR.WARNINGS.NO_SERVICE_CHANNEL);
        }
        if (
          serviceStateChannel &&
          channelLoaded &&
          serviceStateChannel != channelLoaded
        ) {
          pushMessage(
            strings.CONSTRUCTOR.WARNINGS.WRONG_SERVICE_CHANNEL +
              " " +
              getLocalizedChannel(serviceState.data?.channel)
          );
          blockConfirm();
        }
        else if (serviceState.data?.deviceCheckSolution !== workflowLoaded.deviceCheckSolution) {
          // Same service channel, but different Device Check Solution.
          // Device check solution must match
          pushMessage(strings.CONSTRUCTOR.WARNINGS.WRONG_DEVICE_CHECK_SOLUTION + " " + getLocalizedDeviceCheckSolution(serviceState.data?.deviceCheckSolution));
          blockConfirm();
        }
        if(workflowLoaded?.channel !== undefined && workflowLoaded?.channel === Experience.Retail){
          if(workflowLoaded.result?.userSatisfactionSurvey !== undefined){
              let uss = workflowLoaded.result?.userSatisfactionSurvey;
              if(uss.csatSurvey === true || uss.npsSurvey === true || uss.customSurvey === true
               || uss.customCsatRating !== undefined || uss.customCsatRatingScale !== undefined)
              {
              messages.push({
                  text:strings.CONSTRUCTOR.STAGES.RESULT.PIVOTS.USER_SATISFACTION_SURVEY.USER_SATISFACTION_SURVEY_NOT_ALLOWED_ERROR,
                  type: MessageBarType.error })
              blockConfirm();
             }
          }
        }

        // validate assessment modules
        if (!isAssesmentModulesValid(serviceState.data, workflowLoaded)) {
          pushMessage(`${strings.CONSTRUCTOR.WARNINGS.CONFIGURATION_INCORRECT} ${strings.CONSTRUCTOR.WARNINGS.UNSUPPORTED_MODULE}`);
          blockConfirm();
        }

        if (!isCommonOfferValid(workflowLoaded)) {
          pushMessage(`${strings.CONSTRUCTOR.WARNINGS.CONFIGURATION_INCORRECT} ${strings.CONSTRUCTOR.WARNINGS.UNSUPPORTED_MODULE}`);
          blockConfirm();
        }

        if(workflowLoaded.result?.userSatisfactionSurvey !== undefined){
          let uss = workflowLoaded.result?.userSatisfactionSurvey;
          if(uss?.csatSurvey === true && uss?.npsSurvey === true )
          {
              messages.push({
                  text:strings.CONSTRUCTOR.STAGES.RESULT.PIVOTS.USER_SATISFACTION_SURVEY.USER_SATISFACTION_SURVEY_ONLY_ONE_ALLOWED_ERROR,
                  type: MessageBarType.error })
              blockConfirm();
          }
        }
        if (channelLoaded) {
          const identificationModuleFails =
            workflowLoaded.identification.items.map(
              (i) => !CheckIdentificationModuleAccess(channelLoaded, i.method)
            );

          const assessmentModuleFails = Object.keys(
            workflowLoaded.assessment?.inspections || {}
          ).map((key) => {
            return workflowLoaded.assessment?.inspections[key]
              ? !CheckAssesmentModuleAccess(
                  channelLoaded,
                  key as Inspections,
                  serviceState.data?.deviceCheckSolution.toString() || ""
                )
              : false;
          });
          const processStageAccessFails = workflowLoaded.stages?.map(
            (s) => !CheckProcessStageAccess(channelLoaded, s.type)
          );
          const checkSettingFail = (
            check: boolean | undefined,
            settingName: string
          ): boolean =>
            !!check && !CheckWorkflowSettingsAccess(channelLoaded, settingName);

          const workflowSettingsFails =
            checkSettingFail(
              workflowLoaded.isControlTransaction,
              "This is control workflow"
            ) ||
            checkSettingFail(
              workflowLoaded.useGradesCategories,
              "Use grade categories"
            ) ||
            checkSettingFail(
              !!workflowLoaded.deviceTypes,
              "Filter devices by category"
            );

          const checkStageSection = (
            type: ProcessStages,
            section: string,
            option: string
          ) =>
          {
            return !CheckProcessStageSectionOptionAccess(
              channelLoaded,
              type,
              section,
              option
            );
          };

          const identificationSectionCases: StageSectionCase[] = [
            {
              processStage: ProcessStages.Identification,
              value: workflowLoaded.identification.useGroupAsSku,
              section: "Additional",
              option: "Use device group as SKU code",
            },
            {
              processStage: ProcessStages.Identification,
              value: workflowLoaded.identification.moveNextStageOnComplete,
              section: "Additional",
              option: "Skip device identification summary page",
            },
            {
              processStage: ProcessStages.Identification,
              value: workflowLoaded.identification.deviceGroups,
              section: "Device groups",
              option: "Allowed device groups (from the workflow catalog)",
            },
            {
              processStage: ProcessStages.Identification,
              value: workflowLoaded.identification.targetDeviceSelection,
              section: "ui_flow",
              option: "Select target device",
              fixOption: (config: IWorkflow) => {
                if(config.identification?.targetDeviceSelection){
                  delete config.identification.targetDeviceSelection;
                }
              }
            },
            {
              processStage: ProcessStages.Identification,
              value: workflowLoaded.identification.targetDeviceSelection?.information,
              section: "ui_flow",
              option: "Custom information",
              fixOption: (config: IWorkflow) => {
                if(config.identification?.targetDeviceSelection?.information){
                  delete config.identification.targetDeviceSelection.information;
                }
              }
            },
            {
              processStage: ProcessStages.Identification,
              value: workflowLoaded.identification.targetDeviceSelection?.title,
              section: "ui_flow",
              option: "Custom title",
              fixOption: (config: IWorkflow) => {
                if(config.identification?.targetDeviceSelection?.title){
                  delete config.identification.targetDeviceSelection.title;
                }
              }
            }
          ]

          const assessmentSectionCases: StageSectionCase[] = [
            {
              processStage: ProcessStages.Assessment,
              value: workflowLoaded.assessment?.requirements,
              section: "Device Identifiers",
              option: "Require device identifiers",
            },
            {
              processStage: ProcessStages.Assessment,
              value: workflowLoaded.assessment?.ui?.requirements?.title,
              section: "Device Identifiers",
              option: "Use a custom title",
            },
            {
              processStage: ProcessStages.Assessment,
              value: workflowLoaded.assessment?.ui?.requirements?.information,
              section: "Device Identifiers",
              option: "Use custom information",
            },
            {
              processStage: ProcessStages.Assessment,
              value: workflowLoaded.assessment?.requirements?.imei,
              section: "Device Identifiers",
              option: "Requires IMEI",
            },
            {
              processStage: ProcessStages.Assessment,
              value: workflowLoaded.assessment?.requirements?.imei2,
              section: "Device Identifiers",
              option: "Requires IMEI2",
            },
            {
              processStage: ProcessStages.Assessment,
              value: workflowLoaded.assessment?.requirements?.sn,
              section: "Device Identifiers",
              option: "Requires serial number",
            },
            {
              processStage: ProcessStages.Assessment,
              value: workflowLoaded.assessment?.hideDependency,
              section: "Additional",
              option: "Hide dependent module",
            },
            {
              processStage: ProcessStages.Assessment,
              value: workflowLoaded.assessment?.hideSkiped,
              section: "Additional",
              option: "Hide skipped modules",
            },
            {
              processStage: ProcessStages.Assessment,
              value: workflowLoaded.assessment?.collapseDone,
              section: "Additional",
              option: "Collapse completed modules",
            },
            {
              processStage: ProcessStages.Assessment,
              value: workflowLoaded.assessment?.cancelOptions,
              section: "Cancel reasons",
              option: "Use step cancel reasons",
            },
            {
              processStage: ProcessStages.Assessment,
              value: workflowLoaded.assessment?.ui?.buttons?.next?.text,
              section: "Buttons",
              option: "Custom Next button caption",
            },
            {
              processStage: ProcessStages.Assessment,
              value: workflowLoaded.assessment?.ui?.buttons?.next?.teaching,
              section: "Buttons",
              option: "Custom hint text",
            },
            {
              processStage: ProcessStages.Assessment,
              value: workflowLoaded.assessment?.ui?.buttons?.cancel?.text,
              section: "Buttons",
              option: "Custom Cancel button caption",
            },
            {
              processStage: ProcessStages.Assessment,
              value: workflowLoaded.assessment?.ui?.buttons?.back?.text,
              section: "Buttons",
              option: "Custom Back button caption",
            },
            {
              processStage: ProcessStages.Assessment,
              value: workflowLoaded.assessment?.ui?.stageTitle,
              section: "Step UI",
              option: "Use custom step title",
            },
            {
              processStage: ProcessStages.Assessment,
              value: workflowLoaded.assessment?.ui?.header,
              section: "Step UI",
              option: "Use custom step header",
            },
            {
              processStage: ProcessStages.Assessment,
              value: workflowLoaded.assessment?.ui?.information,
              section: "Step UI",
              option: "Use custom step information",
            },
            {
              processStage: ProcessStages.Assessment,
              value: workflowLoaded.assessment?.requireConfirmation,
              section: "Confirmation",
              option: "Use step confirmation",
            },
            {
              processStage: ProcessStages.Assessment,
              value: workflowLoaded.assessment?.ui?.confirmation?.header,
              section: "Confirmation",
              option: "Use custom confirmation header",
            },
            {
              processStage: ProcessStages.Assessment,
              value: workflowLoaded.assessment?.ui?.confirmation?.information,
              section: "Confirmation",
              option: "Use custom confirmation information",
            },
            {
              processStage: ProcessStages.Assessment,
              value: workflowLoaded.assessment?.ui?.confirmation?.buttons?.confirm?.text,
              section: "Confirmation",
              option: "Custom Confirm button caption",
            },
            {
              processStage: ProcessStages.Assessment,
              value: workflowLoaded.assessment?.ui?.confirmation?.buttons?.cancel?.text,
              section: "Confirmation",
              option: "Custom Cancel button caption",
            },
            {
              processStage: ProcessStages.Assessment,
              value: workflowLoaded.assessment?.modules.some((m) => m.config.skipSummary),
              section: "Common settings",
              option: "Skip summary",
            },                                
          ]

          const preOfferSectionCases: StageSectionCase[] = [
            {
              processStage: ProcessStages.PreOffer,
              value: workflowLoaded.commonOffer?.ui?.tradeInDevice,
              section: "Common offer settings",
              option: "Custom trade-in device title",
            },
            {
              processStage: ProcessStages.PreOffer,
              value: workflowLoaded.commonOffer?.withoutPrices,
              section: "Common offer settings",
              option: "Do not stop workflow if no offer found",
            },
            {
              processStage: ProcessStages.PreOffer,
              value: workflowLoaded.commonOffer?.allowDiscount,
              section: "Campaigns/UI settings",
              option: "Allow campaigns",
            },
            {
              processStage: ProcessStages.PreOffer,
              value: workflowLoaded.commonOffer?.discount?.ui?.tradeUpDevice,
              section: "Campaigns/UI settings",
              option: "Custom trade-up device title",
            },
            {
              processStage: ProcessStages.PreOffer,
              value: workflowLoaded.commonOffer?.discount?.ui?.button,
              section: "Campaigns/UI settings",
              option: "Custom Campaign button caption",
            },
            {
              processStage: ProcessStages.PreOffer,
              value: workflowLoaded.commonOffer?.discount?.ui?.header,
              section: "Campaigns/UI settings",
              option: "Custom Campaign button hint header text",
            },
            {
              processStage: ProcessStages.PreOffer,
              value: workflowLoaded.commonOffer?.discount?.ui?.information,
              section: "Campaigns/UI settings",
              option: "Custom Campaign panel information",
            },
            {
              processStage: ProcessStages.PreOffer,
              value: workflowLoaded.commonOffer?.discount?.ui?.placeholder,
              section: "Campaigns/UI settings",
              option: "Custom Campaign panel search input placeholder",
            },
            {
              processStage: ProcessStages.PreOffer,
              value: workflowLoaded.preOffer?.useAnalogs,
              section: "Additional",
              option: "Show device analogs",
            },
            {
              processStage: ProcessStages.PreOffer,
              value: workflowLoaded.preOffer?.useAnalogsChoice,
              section: "Additional",
              option: "Allow analog selection",
            },
            {
              processStage: ProcessStages.PreOffer,
              value: workflowLoaded.preOffer?.showPricesBeforeAssessment,
              section: "Additional",
              option: "Show offer prices",
              fixOption: (config: IWorkflow) => {
                // 'Show offer prices' is available only for retail channel. For other channels it always must be true but disabled.
                if(channelLoaded !== Experience.Retail){
                  config.preOffer = {...config.preOffer, showPricesBeforeAssessment: true};
                }
              }
            },
            {
              processStage: ProcessStages.PreOffer,
              value: workflowLoaded.preOffer?.showBestPriceOnly,
              section: "Additional",
              option: "Show the best price only",
              fixOption: (config: IWorkflow) => {
                // 'Show the best price only' is available only for retail channel. For other channels it always must be true but disabled.
                if(channelLoaded !== Experience.Retail){
                  config.preOffer = {...config.preOffer, showBestPriceOnly: true};
                }
              }
            },
            {
              processStage: ProcessStages.PreOffer,
              value: workflowLoaded.preOffer?.ui?.stageTitle,
              section: "hasOffer",
              option: "Custom message text",
            },
            {
              processStage: ProcessStages.PreOffer,
              value: workflowLoaded.preOffer?.ui?.information,
              section: "hasOffer",
              option: "Custom information text",
            },                
          ]

          const postOfferSectionCases: StageSectionCase[] = [
            {
              processStage: ProcessStages.PostOffer,
              value: workflowLoaded.commonOffer?.ui?.tradeInDevice,
              section: "Common offer settings",
              option: "Custom trade-in device title",
            },
            {
              processStage: ProcessStages.PostOffer,
              value: workflowLoaded.commonOffer?.withoutPrices,
              section: "Common offer settings",
              option: "Do not stop workflow if no offer found",
            },
            {
              processStage: ProcessStages.PostOffer,
              value: workflowLoaded.commonOffer?.allowDiscount,
              section: "Campaigns/UI settings",
              option: "Allow campaigns",
            },
            {
              processStage: ProcessStages.PostOffer,
              value: workflowLoaded.commonOffer?.discount?.ui?.tradeUpDevice,
              section: "Campaigns/UI settings",
              option: "Custom trade-up device title",
            },
            {
              processStage: ProcessStages.PostOffer,
              value: workflowLoaded.commonOffer?.discount?.ui?.button,
              section: "Campaigns/UI settings",
              option: "Custom Campaign button caption",
            },
            {
              processStage: ProcessStages.PostOffer,
              value: workflowLoaded.commonOffer?.discount?.ui?.header,
              section: "Campaigns/UI settings",
              option: "Custom Campaign button hint header text",
            },
            {
              processStage: ProcessStages.PostOffer,
              value: workflowLoaded.commonOffer?.discount?.ui?.information,
              section: "Campaigns/UI settings",
              option: "Custom Campaign panel information",
            },
            {
              processStage: ProcessStages.PostOffer,
              value: workflowLoaded.commonOffer?.discount?.ui?.placeholder,
              section: "Campaigns/UI settings",
              option: "Custom Campaign panel search input placeholder",
            },
            {
              processStage: ProcessStages.PostOffer,
              value: workflowLoaded.postOffer?.autoSelectBestOffer,
              section: "Additional",
              option: "Select best offer autommatically",
            },
            {
              processStage: ProcessStages.PostOffer,
              value: workflowLoaded.postOffer?.ui?.header,
              section: "Step UI",
              option: "Custom post-offer stage subtitle",
            },
            {
              processStage: ProcessStages.PostOffer,
              value: workflowLoaded.postOffer?.ui?.information,
              section: "Step UI",
              option: "Custom information for the post-offer stage",
            },                
          ]

          const contractSectionCases: StageSectionCase[] = [
            {
              processStage: ProcessStages.Contract,
              value: workflowLoaded.contract?.useSubscription,
              section: "Additional",
              option: "Enable marketing consent request",
            },
            {
              processStage: ProcessStages.Contract,
              value: workflowLoaded.contract?.useDaData,
              section: "Additional",
              option: "Use DaData",
            },                
          ]

          const resultSectionCases: StageSectionCase[] = [
            {
              processStage: ProcessStages.Result,
              value: workflowLoaded.result?.contractButton,
              section: "Buttons",
              option: "Show contract button",
            },
            {
              processStage: ProcessStages.Result,
              value: workflowLoaded.result?.contractButton?.icon,
              section: "Buttons",
              option: "Custom Print button icon",
            },
            {
              processStage: ProcessStages.Result,
              value: workflowLoaded.result?.contractButton?.text,
              section: "Buttons",
              option: "Custom Print button caption",
            },
            {
              processStage: ProcessStages.Result,
              value: workflowLoaded.result?.contractButton?.tooltip,
              section: "Buttons",
              option: "Custom Print button tooltip text",
            },
            {
              processStage: ProcessStages.Result,
              value: workflowLoaded.result?.completeFlowBehavior,
              section: "Additional",
              option: "Redirect to external url upon completion",
              fixOption: (config: IWorkflow) => {
                if(config.result?.completeFlowBehavior != null){
                  delete config.result?.completeFlowBehavior;
                }
              }
            },
          ]

          const stageSectionCases: Partial<Record<ProcessStages, StageSectionCase[]>> = {
            [ProcessStages.Identification]: identificationSectionCases,
            [ProcessStages.Assessment]: assessmentSectionCases,
            [ProcessStages.PreOffer]: preOfferSectionCases,
            [ProcessStages.PostOffer]: postOfferSectionCases,
            [ProcessStages.Contract]: contractSectionCases,
            [ProcessStages.Result]: resultSectionCases,
          }

          const invalidStageSectionOptions =
            workflowLoaded.stages?.flatMap((s) => {
              const casesForStage = stageSectionCases[s.type];
              if (!casesForStage) return [];

              return casesForStage.flatMap(({ value, section, option, fixOption }) => {
                if (!value || !checkStageSection(s.type, section, option)) {
                  return [];
                }

                if (fixOption) {
                  fixOption(workflowLoaded);
                  workflowWasFixed = true;
                  return [];
                }

                return [{
                  stageType: s.type,
                  section,
                  option
                }];
              });
            }) || [];

          if (invalidStageSectionOptions.length > 0) {
            invalidStageSectionOptions.forEach(({ stageType, section, option }) => {
              pushMessage(
                `${strings.CONSTRUCTOR.WARNINGS.CONFIGURATION_INCORRECT} ${strings.CONSTRUCTOR.WARNINGS.INCORRECT_SECTION_SETTINGS}: ${getLocalizedStageName(stageType)} / ${section} / ${option}`
              );
            });
            blockConfirm();
          }
          if (!isCheckedRequiredDataExists(workflowLoaded) || isSpecificDataMisplaced(workflowLoaded)) {
            pushMessage(
              `${strings.CONSTRUCTOR.WARNINGS.CONFIGURATION_INCORRECT} ${strings.CONSTRUCTOR.WARNINGS.INCORRECT_SETTINGS}`
            );
            blockConfirm();
          }
          if (workflowSettingsFails) {
            pushMessage(
              `${strings.CONSTRUCTOR.WARNINGS.CONFIGURATION_INCORRECT} ${strings.CONSTRUCTOR.WARNINGS.INCORRECT_SETTINGS}`
            );
            blockConfirm();
          }
          if (identificationModuleFails.some(Boolean)) {
            pushMessage(
              `${strings.CONSTRUCTOR.WARNINGS.CONFIGURATION_INCORRECT} ${strings.CONSTRUCTOR.WARNINGS.UNSUPPORTED_IDENTIFICATION}`
            );
            blockConfirm();
          }
          if (assessmentModuleFails && assessmentModuleFails.some(Boolean)) {
            pushMessage(
              `${strings.CONSTRUCTOR.WARNINGS.CONFIGURATION_INCORRECT} ${strings.CONSTRUCTOR.WARNINGS.UNSUPPORTED_MODULE}`
            );
            blockConfirm();
          }
          if (
            processStageAccessFails &&
            processStageAccessFails.some(Boolean)
          ) {
            pushMessage(
              `${strings.CONSTRUCTOR.WARNINGS.CONFIGURATION_INCORRECT} ${strings.CONSTRUCTOR.WARNINGS.UNSUPPORTED_PROCESS}`
            );
            blockConfirm();
          }
        }
        const scannerItem = workflowLoaded.identification.items.filter(
          (item) => item.method === IdentificationMethods.Scanner
        );
        if (
          workflowLoaded.channel === Experience.Mobile &&
          scannerItem.some(
            (i) =>
              !(i as any)["type"].includes(ScannerIdentificationTypes.Label)
          )
        ) {
          let inspecitonUploadMethods: any[] = [];
          let inspectionDefaultMethod: unknown;
          scannerItem.forEach((i) => {
            const methods = (i as any)["uploadMethods"];
            if (methods) {
              inspecitonUploadMethods = [...inspecitonUploadMethods, methods];
            }
            const defaultInspectionMethod = (i as any)["defaultUploadMethod"];
            if (defaultInspectionMethod) {
              inspectionDefaultMethod = defaultInspectionMethod;
            }
          });

          if (
            inspecitonUploadMethods[0].includes(ScannerUploadMethods.Camera) ||
            inspectionDefaultMethod === ScannerUploadMethods.Camera
          ) {
            pushMessage(
              strings.CONSTRUCTOR.WARNINGS.INCOMPATIBLE_AI_SCANNER_METHOD
            );
            blockConfirm();
          }
          const aiInspection = workflowLoaded.assessment?.modules.filter(
            (item) => item.type === Inspections.AIGrading
          );
          let inspectionItems: any[] = [];
          let gradingDefaultMethod: unknown;
          aiInspection?.forEach((i) => {
            const gradingUploadMethods = (i.config as any)["config"][
              "uploadMethods"
            ];
            if (gradingUploadMethods) {
              inspectionItems = [...inspectionItems, gradingUploadMethods];
            }
            const defaultGradingMethod = (i.config as any)["config"][
              "defaultUploadMethod"
            ];
            if (defaultGradingMethod) {
              gradingDefaultMethod = defaultGradingMethod;
            }
          });

          if (
            (inspectionItems[0] &&
              inspectionItems[0].includes(ScannerUploadMethods.Camera)) ||
            gradingDefaultMethod === ScannerUploadMethods.Camera
          ) {
            pushMessage(
              strings.CONSTRUCTOR.WARNINGS.INCOMPATIBLE_AI_GRADING_METHOD
            );
            blockConfirm();
          }
        }

        let promotionsIds: string[] = [];
        const promotionsResult =
          await portalApi.organization.promotions.getPromotions();
        if (promotionsResult.successed && promotionsResult.data) {
          promotionsIds = promotionsResult.data
            .filter((i) => !i.removed)
            .map((i) => i.id);
        }

        if (workflowLoaded.stages) {
          if (workflowLoaded.commonOffer?.providers) {
            const workflowProvidersCatalogIds =
              workflowLoaded.commonOffer?.providers
                .filter((i) => i.type === OfferProviderTypes.Catalog)
                .map((i) => i.catalogId);
            if (workflowProvidersCatalogIds.length > 0) {
              hasCatalogWarning = !!workflowProvidersCatalogIds.find(
                (cid) => cid && !catalogsIds.includes(cid)
              );
            }
          }
          if (workflowLoaded.commonOffer?.promoProviders) {
            const workflowPromoProvidersPromotionsIds =
              workflowLoaded.commonOffer?.promoProviders.map(
                (i) => i.promotionId
              );
            if (workflowPromoProvidersPromotionsIds.length > 0) {
              hasPromotionWarning = !!workflowPromoProvidersPromotionsIds.find(
                (pid) => !promotionsIds.includes(pid)
              );
            }
          }
          
          const searchFormModule = 
              workflowLoaded.identification?.items.find( i => i.method === IdentificationMethods.SearchForm );
          const searchCatalogId = (searchFormModule as IIdentificationSearchFormConfig)?.searchCatalogId
          
          // if Search in the workflow catalog module enabled then need to validate catalog 
          // to be sure catalog belongs to current organization
          if (searchCatalogId && !catalogsIds.includes(searchCatalogId))
          {
            pushMessage(strings.CONSTRUCTOR.WARNINGS.FORM_SEARCH_CATALOG_WILL_BE_REMOVED, MessageBarType.warning)
          }
          
          if (hasCatalogWarning) {
            pushMessage(
              strings.CONSTRUCTOR.WARNINGS.CATALOG_WILL_BE_REMOVED,
              MessageBarType.warning
            );
          }
          if (hasPromotionWarning) {
            pushMessage(
              strings.CONSTRUCTOR.WARNINGS.CAMPAIGN_WILL_BE_REMOVED,
              MessageBarType.warning
            );
          }

          if (workflowLoaded.selfService && workflowLoaded.selfService.promotions){
            const result = await portalApi.organization.selfServicePromotion.getPromotions()
            if (result.data) {
              // find promotions that are related to current organization and enabled
              const promotionIdsEnabled = result.data
                  .filter(({removed, expirationDate}) =>
                      removed || expirationDate && compareCurrentUTCDateTime(expirationDate, true)
                  )
                  .map(({id}) => id)
              if (workflowLoaded.selfService.promotions.find((id) => !promotionIdsEnabled.includes(id))) {
                pushMessage(
                    strings.CONSTRUCTOR.WARNINGS.CATALOG_WILL_BE_REMOVED,
                    MessageBarType.warning
                );
              }
            }
          }
          if (workflowLoaded.selfService?.repairOffers && workflowLoaded.selfService?.repairOffers.providers){
            const result =
                await portalApi.organization.catalogs.getCatalogsByCatalogType(CatalogTypes.Repair);
            // find all relevant repair offers
            const repairOffersIds = result.data?.filter((i) => !i.removed).map(({id}) => id) || [];
            // if element has provider not from current organization
            if (workflowLoaded.selfService.repairOffers.providers.find(({catalogId}) => !repairOffersIds.includes(catalogId))) {
              pushMessage(
                  strings.CONSTRUCTOR.WARNINGS.REPAIR_OFFERS_WILL_BE_REMOVED,
                  MessageBarType.warning
              );
            }
          }

        } else {
          pushMessage(strings.CONSTRUCTOR.WARNINGS.JSON_NOT_CONTAIN_WORKFLOW);
        }

        if (workflowWasFixed && !hasBlockingErrors) {
          fixedWorkflowFile = new File(
            [JSON.stringify(workflowLoaded, null, 2)],
            file.name,
            { type: file.type || JSON_TYPE }
          );
        }

      } catch (e) {
        // TODO: require proper error message
        pushMessage(strings.CONSTRUCTOR.WARNINGS.INCORRECT_FILE_TYPE);
        blockConfirm();
      }
    } else {
      pushMessage(strings.CONSTRUCTOR.WARNINGS.INCORRECT_FILE_TYPE);
    }

    if (messages.length > 0) {
      setWorflowFileMessages(messages);
    } else {
      setWorflowFileMessages(undefined);
    }

    stopWorkflowCheck();
    return fixedWorkflowFile;
  };

  const onHideImportModal = () => {
    setWorflowFileMessages(undefined);
    hideImportModal();
  };

  const getQrCodeLink = (service: IService): string => {
    return service.channel === Experience.Mobile && service.serviceType === ServiceTypes.SelfService ?
         endpoints.external.selfServiceApp(selfServiceUrl, service.id as number) :
         endpoints.external.applicationWidget(applicationUrl, service.id as number);
  };

  const getService = async (id: number) => {
    setServiceState({ ...serviceState, fetched: false });
    const result = await portalApi.organization.services.getService(id);
        if (result.successed && result.data) {
      setServiceState({
        fetched: true,
        data: result.data,
        qrCodeLink: getQrCodeLink(result.data),
      });
    }
    if (result.errors && result.errors.length > 0) {
      hideConfirmation();
      setError(result.errors[0].description);
    }
  };

  const updateService = async (
    item: IService
  ): Promise<IRequestResult<IService>> => {
    setFormError(undefined);
    startFetch();
    
    const result = await portalApi.organization.services.updateService(item);
    
    if (result.successed && result.data) {
      setServiceState({
        fetched: true,
        data: result.data,
        qrCodeLink: getQrCodeLink(result.data),
      });
      hidePanel();
      stopFetch();
    }
    if (result.errors && result.errors.length > 0) {
      setError(result.errors[0].description);
    }
    return result;
  };

  const updateServiceInformation = async (information: string) => {
    if (serviceState.data?.id) {
      startFetchInfo();
      setInfoWasSaved(false);
      const result =
        await portalApi.organization.services.updateServiceInformation(
          serviceState.data?.id,
          information
        );
      if (result.successed) {
        setServiceState({
          ...serviceState,
          data: { ...serviceState.data, information: information },
        });
        setInfoWasSaved(true);
      }
      if (result.errors && result.errors.length > 0) {
        setError(result.errors[0].description);
      }
      stopFetchInfo();
    }
  };

  const deleteService = async (id: number) => {
    startFetch();
    const result = await portalApi.organization.services.deleteService(id);
    if (result.successed) {
      history.push("/services/configuration");
    }
    if (result.errors && result.errors.length > 0) {
      hideConfirmation();
      setError(result.errors[0].description);
      return;
    }
    if (!result.successed) {
      setError(
        "There is a problem! Service wasn't deleted! Server response false without an error"
      );
    }
    stopFetch();
    hideConfirmation();
  };

  const onFormSubmit = (data: IService) => {
    serviceState.data?.id && updateService(data);
  };

  const onConfigurationImportSuccessed = () => {
    if (serviceState.data?.id) {
      getService(serviceState.data?.id);
    }
  };

  return (
    <Stack verticalFill style={{ position: "relative" }}>
      {!error &&
        ((serviceState.data && (
          <ScrollablePane>
            <Stack verticalFill>
              <Stack.Item>
                <ServiceConfigurationHeader
                  generateIcon={workflowIconGeneration}
                  onEdit={showPanel}
                  item={serviceState.data}
                  onShareWorkflow={showShareWorkflow}
                />
              </Stack.Item>
              <Stack.Item verticalFill>
                <ServiceConfigurationDetails
                  setInformation={updateServiceInformation}
                  retryInfo={() => getService(parseInt(serviceId))}
                  data={serviceState.data}
                  onDelete={showConfirmation}
                  onImportWorkflow={showImportModal}
                  updateService={updateService}
                  infoFetching={infoFetching}
                  isFetching={fetching}
                  infoWasSaved={infoWasSaved}
                  generateIcon={workflowIconGeneration}
                  resetInfoWasSaved={() => setInfoWasSaved(false)}
                />
              </Stack.Item>
            </Stack>
          </ScrollablePane>
        )) || (
          <Stack
            verticalAlign="center"
            verticalFill
            horizontalAlign="center"
            tokens={{ childrenGap: 12 }}
          >
            <Spinner label={strings.SPINNERS.DATA_IS_GETTING} />
          </Stack>
        ))}
      {error && (
        <Stack verticalFill>
          {error.length > 0 && (
            <MessageBar messageBarType={MessageBarType.error}>
              {error}
            </MessageBar>
          )}
          <Stack.Item verticalFill>
            <SomethingWentWrong
              action={() => getService(parseInt(serviceId))}
            />
          </Stack.Item>
        </Stack>
      )}
      <CustomPanel
        isOpen={isPanelOpen}
        onCancel={hidePanel}
        title={strings.ORGANIZATION.SERVICES.SERVICE.EDIT_SERVICE}
      >
        {formError && (
          <MessageBar messageBarType={MessageBarType.error}>
            {formError}
          </MessageBar>
        )}
        {(!formError && !fetching && (
          <ServiceForm
            onSubmit={(data: IService) => onFormSubmit(data)}
            onCancel={hidePanel}
            data={serviceState.data as IService}
          />
        )) || (
          <Stack verticalAlign="center" verticalFill horizontalAlign="center">
            <Spinner label={strings.SPINNERS.DATA_IS_GETTING} />
          </Stack>
        )}
      </CustomPanel>

      <ShareWorkflowModal
        isOpen={isShareWorkflowPanelOpen}
        qrCodeLink={serviceState.qrCodeLink}
        onDismiss={() =>{
          hideShareWorkflow()
        }}/>

      {serviceState.data?.id && (
        <Modal
          isOpen={confirmation}
          onDismiss={hideConfirmation}
          containerClassName={contentStyles.container}
        >
          <div className={contentStyles.header}>
            <span style={{ paddingRight: 32 }}>
              {strings.ORGANIZATION.SERVICES.SERVICE.REMOVE_CONFIRMATION_TITLE}
            </span>
            <IconButton
              styles={iconButtonStyles}
              iconProps={{ iconName: "Cancel" }}
              ariaLabel="Close"
              onClick={hideConfirmation}
            />
          </div>
          <div className={contentStyles.body}>
            <ConfirmationForm
              onConfirm={() =>
                serviceState.data?.id && deleteService(serviceState.data?.id)
              }
              onCancel={hideConfirmation}
              requiredMessageString={
                strings.ORGANIZATION.SERVICES.SERVICE.FIELDS.NAME_REQUIRED
              }
              confirmationCheckString={serviceState.data?.name}
              placeholder={
                strings.ORGANIZATION.SERVICES.SERVICE.FIELDS.NAME_PLACEHOLDER
              }
              confirmText={strings.BUTTONS.TEXT.DELETE}
            >
              <Stack tokens={{ childrenGap: 12 }}>
                <Text>
                  {
                    strings.ORGANIZATION.SERVICES.SERVICE
                      .REMOVE_CONFIRMATION_TEXT1
                  }
                </Text>
                <Text>
                  {
                    strings.ORGANIZATION.SERVICES.SERVICE
                      .REMOVE_CONFIRMATION_TEXT2
                  }
                </Text>
                <Text>
                  {
                    strings.ORGANIZATION.SERVICES.SERVICE
                      .REMOVE_CONFIRMATION_REQUIREMENTS
                  }
                  <b>"{serviceState.data?.name}"</b>
                </Text>
              </Stack>
            </ConfirmationForm>
          </div>
        </Modal>
      )}
      {isImportModalShowed && (
        <ImportFromFile
          type="icon"
          src="FileCode"
          show={isImportModalShowed}
          onHide={onHideImportModal}
          acceptTypeMask={".json"}
          allowedFileTypes={["application/json"]}
          lable={strings.ORGANIZATION.SERVICES.SERVICE.IMPORT_LABEL}
          description={strings.ORGANIZATION.SERVICES.SERVICE.IMPORT_DESCRIPTION}
          iconColor={SharedColors.orangeYellow10}
          title={strings.ORGANIZATION.SERVICES.SERVICE.IMPORT_TITLE}
          sendParams={{ serviceId: serviceId }}
          onSuccessed={onConfigurationImportSuccessed}
          sendMethod={
            portalApi.organization.services.updateServiceConfiguration
          }
          onFileChange={checkWorkflowForUpload}
          messages={workflowFileMessages}
          resetMessages={() => setWorflowFileMessages(undefined)}
          isFetching={workflowIsChecking}
          noReset
          disableConfirm={confirmIsDisabled}
        />
      )}
    </Stack>
  );
};

/** Validates assesment modules */
const isAssesmentModulesValid = (currentService: IService | undefined, workflowLoaded: IWorkflow) : boolean => {
  if (!currentService) return false;

  if (!workflowLoaded.assessment || !workflowLoaded.assessment.modules)
    return true;

  const solution = currentService.deviceCheckSolution;

  const isValid = workflowLoaded.assessment.modules.every(m => {
    // validate DGS module
    if (m.type === Inspections.Diagnostics) {
      const config = m.config as IDiagnosticsConfig;

      // validate dgs mode matches to current solution

      if (solution === DeviceCheckSolutions.PiceaOne && config.mode !== DiagnosticsModes.Piceasoft) return false;
      else if (solution === DeviceCheckSolutions.PiceaMobile && config.mode !== DiagnosticsModes.PiceaMobile) return false;
      else if (solution === DeviceCheckSolutions.WebBased && config.mode !== DiagnosticsModes.WebBased) return false;

    }
    else if( m.type === Inspections.Software) {
      const config = m.config as ISoftwareConfig;
      if (solution === DeviceCheckSolutions.PiceaOne && config.mode !== SoftwareModes.Piceasoft) return false;
      else if (solution === DeviceCheckSolutions.PiceaMobile && config.mode !== SoftwareModes.PiceaMobile) return false;
      else if (solution === DeviceCheckSolutions.WebBased && config.mode !== SoftwareModes.WebBased) return false;
    }
    return true;
  });

  return isValid;
}

/** Validates commonOffer */
const isCommonOfferValid = (workflowLoaded: IWorkflow) : boolean => {
  if (!workflowLoaded.commonOffer)
    return true;

  const providers = workflowLoaded.commonOffer.providers;

  if (!providers || providers.length === 0)
    return true;

  // item code is unique, verify no duplicates
  const codes = providers.map(item => item.code);
  if (hasDuplicates(codes)) {
    return false;
  }

  // determine required string fields by OfferProviderTypes
  const requiredStringFieldsByType = {
    [OfferProviderTypes.Offer]: ['name', 'code', 'endpoint', 'currency'],
    [OfferProviderTypes.Catalog]: ['name', 'code', 'catalogId'],
  }

  for (let i = 0; i < providers.length; i++) {
    const item = providers[i];

    if (item.type !== OfferProviderTypes.Offer && item.type !== OfferProviderTypes.Catalog) {
      return false;
    }

    // verify all required string fields by type (just verifies that string value exists)
    for (const fieldName of requiredStringFieldsByType[item.type]) {
      if (!verifyRequiredStringField(item, fieldName)) {
        return false;
      }
    }

    // validate commissionType and commissionValue
    if (typeof item.commissionType === 'number') {
      if (item.commissionType === CommissionType.Percentage) {
        if (typeof item.commissionValue !== 'number' || (item.commissionValue < 0 || item.commissionValue > 100)) {
          return false;
        }
      }
      else if (item.commissionType === CommissionType.Value) {
        if (typeof item.commissionValue !== 'number' || item.commissionValue < 0) {
          return false;
        }
      }
      else {
        return false;
      }
    }
    else {
      // ok, no commissions
      item.commissionType = undefined;
      item.commissionValue = undefined;
    }

    if (typeof item.index === 'number') {
      // index defined, verify it
      if (item.index !== i) {
        return false;
      }
    }
    else {
      // index not defined, generate
      item.index = i;
    }

  }

  // all seems to be ok
  return true;
}

/** Returns true if duplcate values found from array */
function hasDuplicates<T>(array: T[]): boolean {
  const uniqueValues: Set<T> = new Set();
  for (const value of array) {
    if (uniqueValues.has(value)) {
        return true;
    }
    uniqueValues.add(value);
  }
  return false;
}

/**
 * Verifies that obj contains fieldName and it is valid string.
 * @param obj any object
 * @param fieldName field to test
 * @returns true if valid
 */
function verifyRequiredStringField(obj: any, fieldName: string): boolean {
  if (fieldName in obj) {
      const fieldValue = obj[fieldName];
      if (fieldValue) {
          if (typeof fieldValue !== 'string' || fieldValue.trim().length === 0) {
              return false;
          }
          return true;
      }
  }
  return false;
}

const theme = getTheme();

const contentStyles = mergeStyleSets({
  container: {
    display: "flex",
    flexFlow: "column nowrap",
    alignItems: "stretch",
    width: 480,
    borderRadius: 6,
  },
  header: [
    theme.fonts.large,
    {
      flex: "1 1 auto",
      borderTop: `none`,
      color: theme.palette.neutralPrimary,
      display: "flex",
      alignItems: "center",
      fontWeight: FontWeights.semibold,
      padding: "12px 12px 14px 24px",
    },
  ],
  body: {
    position: "relative",
    flex: "4 4 auto",
    padding: "0px 24px 24px 24px",
    overflowY: "hidden",
    selectors: {
      p: { margin: "14px 0" },
      "p:first-child": { marginTop: 0 },
      "p:last-child": { marginBottom: 0 },
    },
  },
});
const iconButtonStyles = {
  root: {
    color: theme.palette.neutralPrimary,
    marginLeft: "auto",
    marginTop: "4px",
    marginRight: "2px",
  },
  rootHovered: {
    color: theme.palette.neutralDark,
  },
};
